import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { BroadcastFormValues } from "components/UnifiedBroadcast/UnifiedBroadcast";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { BROADCAST_TEMPLATE_TYPE } from "constants/broadcast";
import { useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import {
  useCreateChatBroadcastMutation,
  useGetBroadcastChannelsCountMutation,
} from "store/apis/broadcast";
import { BackendError } from "types/utils/error";
import { BroadcastCsv } from "./BroadcastCsvUpload";
import { BroadcastMessageTemplates } from "./BroadcastMessageTemplates";
import { BroadcastCall } from "components/BroadcastCall/BroadcastCall";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { BroadcastCountDetails } from "types/Models/broadcast";
import { ListToSentence } from "components/DesignSystem/ListToSentence/ListToSentence";

const getDynamicBroadcastMessage = (countData: BroadcastCountDetails) => {
  if (!countData) return "";

  const messages = [];

  if (countData.chat_count > 0) {
    messages.push(
      <>
        <b>{countData.chat_count}</b> chat message
        {countData.chat_count === 1 ? "" : "s"}
      </>
    );
  }

  if (countData.whatsapp_count > 0) {
    messages.push(
      <>
        <b>{countData.whatsapp_count}</b> WhatsApp message
        {countData.whatsapp_count === 1 ? "" : "s"}
      </>
    );
  }

  if (countData.email_count > 0) {
    messages.push(
      <>
        <b>{countData.email_count}</b> email
        {countData.email_count === 1 ? "" : "s"}
      </>
    );
  }

  if (countData.ivr_count > 0) {
    messages.push(
      <>
        <b>{countData.ivr_count}</b> phone call
        {countData.ivr_count === 1 ? "" : "s"}
      </>
    );
  }

  if (messages.length === 0)
    return "This broadcast will not send any messages.";

  return (
    <>
      This broadcast will trigger and send <ListToSentence items={messages} />.
      Are you sure you want to proceed?
    </>
  );
};

const defaultTemplateFields = {
  message: "",
  email_template_id: "",
  [BROADCAST_TEMPLATE_TYPE.email]: {},
  whatsapp_template_id: "",
  [BROADCAST_TEMPLATE_TYPE.whatsapp]: {},
  [BROADCAST_TEMPLATE_TYPE.chat]: {},
  [BROADCAST_TEMPLATE_TYPE.ivrCall]: {
    scheduled_date: dayjs(new Date()).format(YYYY_MM_DD),
    slot: "",
    ivr_message: `Hey {{team_member_name}}, this is a gentle reminder to please fill up the complete details of the Form 1120 as per {{company_name}} so that we can proceed with the filing at the earliest, thank you`,
    scheduled_time_start: "",
    scheduled_time_end: "",
  },
};

export const BroadcastMessage = ({
  isOpen,
  close,
}: {
  isOpen: boolean;
  close: () => void;
}) => {
  const [messageCSVFile, setMessageCSVFile] = useState<File | null>(null);
  const [ivrCSVFile, setIvrCSVFile] = useState<File | null>(null);
  const [messageChannelLength, setMessageChannelLength] = useState<number>(0);
  const [ivrChannelLength, setIvrChannelLength] = useState<number>(0);
  const { values } = useFormikContext<BroadcastFormValues>();
  const {
    broadcastViaChat,
    broadcastViaEmail,
    broadcastViaWhatsapp,
    broadcastViaIvrCall,
    broadcastVia,
    title,
  } = values;
  const broadcastTemplateModal = useModal();
  const sendBroadcastModal = useModal();
  const [templateFields, setTemplateFields] = useState<
    Record<string, Record<string, any> | string>
  >(defaultTemplateFields);

  const { successToast, alertToast } = useToast();

  const [
    getBroadcastChannelCount,
    { data: countData, isLoading: isGettingCount, isSuccess: isCountSuccess },
  ] = useGetBroadcastChannelsCountMutation();

  const [createBroadcast, { isLoading: broadcasting }] =
    useCreateChatBroadcastMutation();

  const broadcastMessage = async () => {
    try {
      const emailFields = broadcastViaEmail
        ? {
            email_template_id: templateFields.email_template_id as string,
            email_template_variables: JSON.stringify(
              templateFields.email_template_variables as Record<string, any>
            ),
          }
        : {};

      const whatsappFields = broadcastViaWhatsapp
        ? {
            whatsapp_template_id: templateFields.whatsapp_template_id as string,
            whatsapp_template_variables: JSON.stringify(
              templateFields.whatsapp_template_variables as Record<string, any>
            ),
          }
        : {};

      const ivrFields = templateFields[
        BROADCAST_TEMPLATE_TYPE.ivrCall
      ] as Record<string, any>;

      await createBroadcast({
        broadcast_account: broadcastVia,
        chat_message: templateFields.message as string,
        should_send_whatsapp: broadcastViaWhatsapp,
        channels_csv: messageCSVFile,
        ivr_csv: ivrCSVFile,
        should_send_email: broadcastViaEmail,
        ivr_message: ivrFields.ivr_message,
        scheduled_date: ivrFields.scheduled_date,
        scheduled_time_start: ivrFields.scheduled_time_start,
        scheduled_time_end: ivrFields.scheduled_time_end,
        should_send_ivr: broadcastViaIvrCall,
        broadcast_name: title,
        should_send_chat: broadcastViaChat,
        ...emailFields,
        ...whatsappFields,
      }).unwrap();
      successToast({ message: "Broadcast initiated successfully" });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const openSendBroadcastModal = async () => {
    try {
      sendBroadcastModal.open();
      const ivrFields = templateFields[
        BROADCAST_TEMPLATE_TYPE.ivrCall
      ] as Record<string, any>;

      await getBroadcastChannelCount({
        chat_message: templateFields.message as string,
        should_send_whatsapp: broadcastViaWhatsapp,
        channels_csv: messageCSVFile,
        ivr_csv: ivrCSVFile,
        should_send_email: broadcastViaEmail,
        ivr_message: ivrFields.message_text,
        should_send_ivr: broadcastViaIvrCall,
        should_send_chat: broadcastViaChat,
      }).unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handleBroadcastTemplateModalClose = () => {
    broadcastTemplateModal.close();
    setTemplateFields(defaultTemplateFields);
  };

  const getBroadcastMethodsLabel = () => {
    const methods = [];
    if (broadcastViaChat) methods.push("chat");
    if (broadcastViaEmail) methods.push("email");
    if (broadcastViaWhatsapp) methods.push("WhatsApp");

    return methods.length > 0 ? methods.join(" / ") : "broadcast";
  };

  const showBroadcastMessageUpload =
    broadcastViaChat || broadcastViaEmail || broadcastViaWhatsapp;

  const disabled =
    (showBroadcastMessageUpload && messageChannelLength === 0) ||
    (broadcastViaIvrCall && ivrChannelLength === 0);

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="xl">
          <Modal.Header>
            <Modal.Title>Broadcast Message</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-h-full">
            <DashboardContainer className="t-flex t-flex-col t-gap-6 t-h-full">
              {showBroadcastMessageUpload && (
                <BroadcastCsv
                  title={`Upload CSV File for ${getBroadcastMethodsLabel()} broadcast`}
                  file={messageCSVFile}
                  setChannelLength={setMessageChannelLength}
                  setCSVFile={setMessageCSVFile}
                />
              )}
              {messageChannelLength > 0 && ivrChannelLength > 0 && (
                <div className="t-border-dashed t-border-neutral-20 t-border-[0.5px]" />
              )}
              {broadcastViaIvrCall && (
                <BroadcastCall
                  file={ivrCSVFile}
                  setChannelLength={setIvrChannelLength}
                  setCSVFile={setIvrCSVFile}
                />
              )}
            </DashboardContainer>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Cancel</Button>
            </Modal.RawClose>

            <Button
              customType="primary"
              disabled={disabled}
              onClick={broadcastTemplateModal.open}
            >
              Next
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      {broadcastTemplateModal.isOpen && (
        <BroadcastMessageTemplates
          {...broadcastTemplateModal}
          close={handleBroadcastTemplateModalClose}
          templateFields={templateFields}
          setTemplateFields={setTemplateFields}
          openSendBroadcast={() => openSendBroadcastModal()}
          ivrCSVFile={ivrCSVFile}
        />
      )}
      <Modal.Root
        open={sendBroadcastModal.isOpen}
        onOpenChange={sendBroadcastModal.close}
      >
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Send broadcast</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <Async.Root
              {...{
                isLoading: isGettingCount,
                isSuccess: isCountSuccess,
                isEmpty: false,
              }}
            >
              <Async.Empty>
                <></>
              </Async.Empty>
              <Async.Success>
                {countData && (
                  <p className="t-text-text-60 t-text-body-lg">
                    {getDynamicBroadcastMessage(countData)}
                  </p>
                )}
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button>Go Back</Button>
            </Modal.RawClose>
            <Button
              customType="danger"
              type="submit"
              isLoading={broadcasting}
              disabled={broadcasting}
              onClick={broadcastMessage}
            >
              Send broadcast
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
